// src/pages/TeamsPage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { subscribeToSectionSettings } from "../lib/siteSettings";

// Division images
import CDLImage from "../assets/divisions/cdl.jpeg";
import U18Image from "../assets/divisions/u18.jpeg";
import WarzoneImage from "../assets/divisions/warzone.jpeg";
import FemaleImage from "../assets/divisions/female.jpeg";

const teams = [
  {
    id: "cdl",
    name: "CDL",
    subtitle: "Call of Duty League",
    description: "Our competitive Call of Duty roster featuring top Caribbean talent",
    image: CDLImage,
    link: "/roster/team/cdl",
    comingSoon: false,
    hasImage: true,
  },
  {
    id: "warzone",
    name: "Warzone",
    subtitle: "Battle Royale / Resurgence",
    description: "Our Battle Royale and Resurgence competitive roster",
    image: WarzoneImage,
    link: "/roster/team/warzone",
    comingSoon: false,
    hasImage: true,
  },
  // Coming Soon — always rendered last so they sit together at the bottom
  {
    id: "female",
    name: "Female",
    subtitle: "Female Division",
    description: "Representing Caribbean women in competitive gaming",
    image: FemaleImage,
    link: "/roster/team/female",
    comingSoon: false,
    hasImage: true,
  },
  {
    id: "u18",
    name: "U18",
    subtitle: "Under 18 Division",
    description: "The next generation of Prime Valor competitive talent",
    image: U18Image,
    link: "/roster/team/u18",
    comingSoon: false,
    hasImage: true,
  },
];

// Only shown when the team switches the Graduates page on from the admin panel.
const graduatesTeam = {
  id: "graduates",
  name: "Graduates",
  subtitle: "Gone Pro",
  description: "Past Prime Valor members now competing on the professional stage",
  image: null,
  link: "/roster/team/graduates",
  comingSoon: false,
  hasImage: false, // gradient card until a division image is provided
};

function TeamsPage() {
  const [sections, setSections] = useState(null);
  useEffect(() => subscribeToSectionSettings(setSections), []);

  const visibleTeams = sections?.graduatesEnabled ? [...teams, graduatesTeam] : teams;

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            Our <span className="text-red-600">Teams</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Select a division to view our roster
          </p>
        </div>
      </section>

      {/* Teams Grid - 2x3 Layout */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {visibleTeams.map((team) => (
              <Link
                to={team.comingSoon ? "#" : team.link}
                key={team.id}
                className={`group relative aspect-[4/3] overflow-hidden rounded-lg ${
                  team.comingSoon ? "cursor-default" : "cursor-pointer"
                }`}
                onClick={(e) => team.comingSoon && e.preventDefault()}
              >
                {/* Background - Image or Gradient */}
                <div className="absolute inset-0">
                  {team.hasImage ? (
                    <img
                      src={team.image}
                      alt={team.name}
                      className={`w-full h-full object-cover transition-transform duration-700 ${
                        team.comingSoon ? "grayscale" : "group-hover:scale-110"
                      }`}
                    />
                  ) : (
                    /* CSS gradient for Creators card */
                    <div className="w-full h-full bg-gradient-to-br from-gray-900 via-red-950/40 to-black">
                      <div className="absolute inset-0 opacity-10"
                        style={{
                          backgroundImage: `linear-gradient(rgba(220,38,38,0.3) 1px, transparent 1px),
                                            linear-gradient(90deg, rgba(220,38,38,0.3) 1px, transparent 1px)`,
                          backgroundSize: '30px 30px'
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                {/* Overlay */}
                <div
                  className={`absolute inset-0 transition-all duration-500 ${
                    team.comingSoon
                      ? "bg-black/70"
                      : team.hasImage
                      ? "bg-black/60 group-hover:bg-black/30"
                      : "bg-black/20 group-hover:bg-black/10"
                  }`}
                ></div>

                {/* Red glow on hover */}
                {!team.comingSoon && (
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-red-600/30 via-transparent to-transparent"></div>
                )}

                {/* Border */}
                <div
                  className={`absolute inset-0 border-2 rounded-lg transition-all duration-500 ${
                    team.comingSoon
                      ? "border-gray-700"
                      : "border-transparent group-hover:border-red-600/50"
                  }`}
                ></div>

                {/* Coming Soon Badge */}
                {team.comingSoon && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded">
                    Coming Soon
                  </div>
                )}

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <div
                    className={`transform transition-transform duration-500 ${
                      team.comingSoon ? "" : "group-hover:translate-y-[-8px]"
                    }`}
                  >
                    <span className="text-red-500 text-xs font-semibold uppercase tracking-wider mb-1 block">
                      {team.subtitle}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-black uppercase text-white mb-2 tracking-tight">
                      {team.name}
                    </h2>
                    <p
                      className={`text-gray-300 text-sm max-w-xs transition-opacity duration-500 ${
                        team.comingSoon
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                    >
                      {team.description}
                    </p>

                    {!team.comingSoon && (
                      <div className="mt-3 inline-flex items-center gap-2 text-white text-sm font-semibold uppercase tracking-wide opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <span>View Roster</span>
                        <svg
                          className="w-4 h-4 transform group-hover:translate-x-2 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default TeamsPage;
